import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { Flex } from "@redotech/arbiter/flex";
import { Text } from "@redotech/arbiter/text";
import { useTranslation } from "@redotech/redo-i18n";
import { memo, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LandingHeader } from "../components/layouts/landing-header";
import { getDashboardUrl } from "../core/auth";
import { useAuth } from "../core/auth-provider";
import { useMerchantInfo } from "../hooks/use-merchant-info";
import { useProductProgress } from "../hooks/use-product-progress";
import {
  getProductsForFeatureFlags,
  PRODUCTS_BY_ID,
  type ProductEntry,
} from "../products";
import * as styles from "./landing-page.module.css";
import { LandingProductPicker } from "./landing-product-picker";
import { LandingProductPitch } from "./landing-product-pitch";
import {
  getDefaultLandingProductId,
  getLandingProductDestination,
  getLandingProductSections,
} from "./landing-products";
import { LandingSkeleton } from "./landing-skeleton";

export const LandingPage = memo(function LandingPage() {
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const { merchantInfo, enabledProducts, featureFlags } = useMerchantInfo();
  const { productProgress } = useProductProgress();
  const [selectedProductId, setSelectedProductId] = useState<string>();

  const products = useMemo(
    () => getProductsForFeatureFlags(featureFlags),
    [featureFlags],
  );
  const sections = useMemo(
    () => getLandingProductSections(products, enabledProducts),
    [enabledProducts, products],
  );
  const defaultProductId = useMemo(
    () => getDefaultLandingProductId(sections, enabledProducts),
    [enabledProducts, sections],
  );
  const effectiveSelectedProductId = selectedProductId ?? defaultProductId;
  const selectedProduct = useMemo(
    () => products.find((product) => product.id === effectiveSelectedProductId),
    [effectiveSelectedProductId, products],
  );
  const catalogProduct = selectedProduct
    ? PRODUCTS_BY_ID.get(selectedProduct.id)
    : undefined;
  const selectedProductIsActive = selectedProduct
    ? (enabledProducts?.[selectedProduct.id] ?? false)
    : false;
  const selectedProductRequiresGuidedSetup =
    !!selectedProduct &&
    !selectedProductIsActive &&
    !selectedProduct.selfServe &&
    !selectedProduct.externalRedirectPath;
  const continueButtonText = selectedProductIsActive
    ? t("Open dashboard")
    : selectedProductRequiresGuidedSetup
      ? t("Schedule a call", { context: "book a call with Redo" })
      : t("Continue");

  const handleSelect = useCallback((product: ProductEntry) => {
    setSelectedProductId(product.id);
  }, []);

  const handleContinue = useCallback(() => {
    if (!selectedProduct) {
      return;
    }
    const destination = getLandingProductDestination(
      selectedProduct,
      enabledProducts?.[selectedProduct.id] ?? false,
      window.location.search,
    );
    if (destination.type === "dashboard") {
      window.location.href = getDashboardUrl(auth?.token);
      return;
    }
    if (destination.type === "dashboard-path") {
      window.location.href = getDashboardUrl(auth?.token, destination.path);
      return;
    }
    navigate(destination.path, { state: { from: "/" } });
  }, [auth, enabledProducts, navigate, selectedProduct]);

  if (!merchantInfo || !enabledProducts || !productProgress) {
    return <LandingSkeleton />;
  }

  return (
    <Flex bgColor="secondary" className={styles.shell} dir="column">
      <Flex
        bgColor="primary"
        borderColor="primary"
        borderRadius="2xl"
        borderStyle="solid"
        borderWidth="1px"
        className={styles.frame}
        dir="column"
        overflow="hidden"
        shadow="lg"
      >
        <LandingHeader
          email={merchantInfo.email}
          logoUrl={merchantInfo.logoUrl}
          storeName={merchantInfo.storeName}
        />
        <Flex
          as="main"
          className={styles.viewport}
          dir="column"
          flex={1}
          minH="none"
          overflow="hidden"
        >
          <Flex
            className={styles.content}
            dir="column"
            flex={1}
            gap="5xl"
            minH="none"
            mx="auto"
            w="full"
          >
            <Flex dir="column" gap="md" shrink={0}>
              <Text
                as="h1"
                fontSize="4xl"
                fontWeight="medium"
                textColor="primary"
              >
                {t("Which product would you like to set up?")}
              </Text>
              <Text fontSize="md" textColor="secondary">
                {t(
                  "Choose one product to see its setup path before continuing.",
                )}
              </Text>
            </Flex>
            <Flex className={styles.pickerGrid} flex={1} gap="5xl" minH="none">
              <LandingProductPicker
                enabledProducts={enabledProducts}
                onSelect={handleSelect}
                productProgress={productProgress}
                sections={sections}
                selectedProductId={effectiveSelectedProductId}
              />
              <LandingProductPitch
                catalogProduct={catalogProduct}
                isActive={selectedProductIsActive}
                product={selectedProduct}
                progress={
                  selectedProduct
                    ? productProgress[selectedProduct.id]
                    : undefined
                }
              />
            </Flex>
          </Flex>
        </Flex>
        <Flex
          className={styles.actions}
          justify="flex-end"
          px="7xl"
          py="4xl"
          shrink={0}
        >
          <RedoButton
            dataTestId="landing-continue"
            disabled={!selectedProduct}
            hierarchy="primary"
            onClick={handleContinue}
            size="md"
            text={continueButtonText}
          />
        </Flex>
      </Flex>
    </Flex>
  );
});
